import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowUpRight, ArrowRight } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { featuredProjects } from '@/data/projects';
import { useCardTilt } from '@/hooks/use-card-tilt';
import { useTypingAnimation } from '@/hooks/use-typing-animation';
import { useFadeInObserver } from '@/hooks/use-fade-in-observer';

const PortfolioSection = () => {
  const { ref: headingRef, displayedText } = useTypingAnimation('My Recent Works', 60);
  
  const navigate = useNavigate();
  
  const ProjectCard = ({ project, index }: { project: any; index: number }) => {
    const { ref: tiltRef, tiltStyle, handleMouseMove, handleMouseLeave } = useCardTilt();
    const { ref: fadeRef, isVisible } = useFadeInObserver(index * 100);

    return (
      <div
        ref={fadeRef}
        className={`transition-all duration-700 ${
          isVisible 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-8'
        }`}
      >
        <Card
          ref={tiltRef}
          className="group overflow-hidden border-border cursor-pointer h-full"
          style={{
            ...tiltStyle,
            transformStyle: 'preserve-3d',
          }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onClick={() => navigate(project.url)}
        >
          <div className="relative h-48 overflow-hidden bg-muted" style={{ transform: 'translateZ(20px)' }}>
            <img 
              src={project.image} 
              alt={project.title}
              className="w-full h-full object-cover transition-all duration-500 group-hover:scale-110 group-hover:rotate-2"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
              <ArrowUpRight className="w-12 h-12 text-white animate-bounce-subtle" />
            </div>
          </div>
          <CardContent className="p-6" style={{ transform: 'translateZ(30px)' }}>
            <Badge variant="secondary" className="mb-3 transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
              {project.category}
            </Badge>
            <h3 className="text-xl font-bold mb-2 transition-colors group-hover:text-primary">{project.title}</h3>
            <p className="text-muted-foreground mb-4">{project.description}</p>
            <div className="flex flex-wrap gap-2">
              {project.tags.map((tag: string, tagIndex: number) => (
                <span
                  key={tagIndex}
                  className="text-xs px-3 py-1 rounded-full bg-secondary text-secondary-foreground transition-all hover:bg-primary hover:text-primary-foreground"
                >
                  {tag}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Show only first 6 projects on homepage
  const projects = featuredProjects.slice(0, 6);

  return (
    <section id="portfolio" className="py-24">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16 animate-fade-in">
          <p className="text-primary font-semibold mb-2 uppercase tracking-wider text-sm">Portfolio</p>
          <h2 ref={headingRef as any} className="text-4xl md:text-5xl font-bold mb-4 min-h-[3rem]">
            {displayedText}
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Explore my latest projects showcasing creativity, innovation, and technical excellence.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <ProjectCard key={index} project={project} index={index} />
          ))}
        </div>

        {/* See More Button */}
        <div className="text-center mt-12">
          <Link to="/projects">
            <Button variant="outline" size="lg" className="group">
              See All Projects
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PortfolioSection;
